
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import * as queryString from 'query-string'
import { QUERY_STRING_OPTIONS } from '../helpers/constants'

interface IParsedQuery<T = string> {
  [key: string]: T | T[] | null | undefined;
}

interface IItem {
  id: string;
  image: string;
  imageAltTag: string;
  title: string;
  url: string;
  [key: string]: string | string[];
}

type ItemProperties = keyof IItem

const PRODUCT_CATEGORY_KEY = 'productCategoryIds'
const ENGINEERING_PAGE_KEY = 'engineeringPageIds'

@Component({
  template: `
    <div class="overview-with-filters container overview-with-list-items"><button class="filter-button mb gray3--text" v-if="Object.keys(filters).length &gt; 0" @click="openFilters"><i class="icons8-slider"></i>{{ openFiltersButtonText }}
            <transition name="fade"><span class="counter" v-if="amountOfSelectedFilterValues &gt; 0"> {{ amountOfSelectedFilterValues }}</span></transition>
        </button>
        <transition name="fade" mode="out-in">
            <div class="no-results text-center gray3--text" v-if="filteredItems.length === 0" key="noResult">{{ filteringNoResults }}</div>
            <div class="list" v-else="v-else" key="list">
                <slot :item-ids-to-show="itemIdsToShow"></slot>
            </div>
        </transition>
        <transition-expand><button class="show-more-button gray3--text" v-if="itemIdsToShow.length &lt; filteredItems.length" @click="amountToShow += itemsPerPage">{{ overviewShowMoreButtonText }}<i class="icons8-chevron-down-2 ml-025"></i></button></transition-expand>
        <transition name="from-left">
            <div class="filters-popup pa white" v-if="showFilters" v-click-outside="closeFilters">
                <div class="top flex flex-justify-between">
                    <div class="filters-popup-title flex flex-align-center"><i class="icons8-slider"></i>{{ openFiltersButtonText }}</div><button class="close-button" @click="closeFilters"><i class="icons8-delete"></i></button>
                </div>
                <div class="bottom">
                    <div class="filter" v-for="(values, key) in filters">
                        <div class="filter-title gray3--text">{{ key | t('filterTitles') }}</div>
                        <div class="form-field form-field--checkbox mt-05" v-for="value in values"><input type="checkbox" :id="value" v-model="selectedFilters[key]" :value="value" /><label :for="value">{{ getCustomLabel(key, value) }}</label></div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
    `
})


export default class OverviewWithFilters extends Vue {
  @Prop({ default: () => [] }) items: IItem[]
  @Prop() filterProperties: ItemProperties[]
  @Prop({ default: 12 }) itemsPerPage: number
  @Prop() productCategories: { code: string; title: string }[]
  @Prop() engineeringPages: { id: string; pageTitle: string }[]
  @Prop() openFiltersButtonText: string
  @Prop() filteringNoResults: string
  @Prop() overviewShowMoreButtonText: string

  selectedFilters: { [key in ItemProperties]?: string[] } = {}

  filters: { [key in ItemProperties]?: string[] } = {}

  showFilters = false

  filtersAreSet = false

  amountToShow = this.itemsPerPage

  get amountOfSelectedFilterValues(): number {
    return Object.values(this.selectedFilters).flat().length
  }

  get filteredItems(): IItem[] {
    if (this.amountOfSelectedFilterValues === 0) return this.items

    return this.items.filter(item => {
      for (const filterProp in this.selectedFilters) {
        const filterPropTyped = filterProp as ItemProperties
        const selectedValues = this.selectedFilters[filterPropTyped]

        if (!!selectedValues && selectedValues.length > 0) {
          const itemValue = item[filterPropTyped]
          const matchesFilters = Array.isArray(itemValue) ?
            selectedValues.some(value => itemValue.map(itemValue => itemValue.toLowerCase()).includes(value))
            : selectedValues.includes(itemValue.toLowerCase())
          if (!matchesFilters) return false
        }
      }
      return true
    })
  }

  get itemIdsToShow(): string[] {
    return this.filteredItems.slice(0, this.amountToShow).map(item => item.id)
  }

  setFilters(): void {
    if (!this.filterProperties) return

    // query-string types dont work in vue file -> queryString.ParsedQuery
    const queryObject: IParsedQuery = queryString.parse(window.location.search, QUERY_STRING_OPTIONS)

    // set all possible filters from existing items
    this.filterProperties.forEach(filterProp => {
      const filterPropValues = this.items
        .map(item => item[filterProp])
        .flat()
        .filter(value => {
          // filter out categories/pages which do not exist in the workcontext
          if (filterProp === PRODUCT_CATEGORY_KEY) return !!value && this.productCategories.some(category => category.code === value)
          if (filterProp === ENGINEERING_PAGE_KEY) return !!value && this.engineeringPages.some(page => page.id === value)
          return !!value
        }) as string[]

      if (filterPropValues.length > 0) {

        const lowerCaseFilterValues = filterPropValues.map(filterValue => filterValue.toLowerCase())
        // create a list with unique items
        this.filters[filterProp] = [...new Set(lowerCaseFilterValues)]
      }
    })

    // set arrays on this.selectedFilters to model properties to
    Object.keys(this.filters).forEach(filterKey => {
      const filterKeyTyped = filterKey as ItemProperties
      this.$set(this.selectedFilters, filterKeyTyped, [])

      // get filters from query
      const filteredValues = queryObject[filterKeyTyped]

      //  set filters from query in this.selectedFilters
      if (filteredValues) {
        if (Array.isArray(filteredValues)) {
          filteredValues.forEach(filterValue => {
            this.addValueToFilters(filterKeyTyped, filterValue)
          })
        } else {
          this.addValueToFilters(filterKeyTyped, filteredValues)
        }
      }
    })

    this.$nextTick(() => this.filtersAreSet = true)
  }

  addValueToFilters(key: ItemProperties, filterValue: string): void {
    const lowerCaseFilterValue = filterValue.toLowerCase()

    if (this.filters[key]?.includes(lowerCaseFilterValue)) {
      this.selectedFilters[key]?.push(lowerCaseFilterValue)
    }
  }

  openFilters(): void {
    this.showFilters = true
    this.$store.commit('openContentOverlay')
  }

  closeFilters(): void {
    this.showFilters = false
    this.$store.commit('closeContentOverlay')
  }

  setSearchQuery() {
    const filtersCopy = JSON.parse(JSON.stringify(this.selectedFilters))
    const newRelativePathQuery = window.location.pathname + (this.amountOfSelectedFilterValues > 0 ? `?${queryString.stringify(filtersCopy, QUERY_STRING_OPTIONS)}` : '')
    // dont remember all filter steps
    window.history.replaceState(null, '', newRelativePathQuery)
  }

  getCustomLabel(filterKey: string, value: string): string {
    if (filterKey === PRODUCT_CATEGORY_KEY && this.productCategories) {
      const category = this.productCategories.find(category => category.code.toUpperCase() === value.toUpperCase())

      return category?.title || value
    }

    if (filterKey === ENGINEERING_PAGE_KEY && this.engineeringPages) {
      const page = this.engineeringPages.find(page => page.id === value)
      return page?.pageTitle || value
    }

    return value
  }

  created() {
    this.setFilters()
  }

  @Watch('selectedFilters', { deep: true })
  onSelectedFiltersChange() {
    // filters need to be initialized which also triggers the watch on selectedFilters
    if (this.filtersAreSet) this.setSearchQuery()
  }
}
